import * as React from 'react';
import {
    useReactTable,
    getCoreRowModel,
    ColumnFiltersState,
    SortingState,
    getSortedRowModel,
    getFilteredRowModel,
    getFacetedUniqueValues,
    ColumnDef
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

// Components
import { fuzzyFilter, multiSelectFilter } from './helpers';

import { TableTemplate } from './template';
import { ICommonTableProps } from './types';

interface TableProps<T> extends ICommonTableProps<T> {
    columns: ColumnDef<T, any>[];
    defaultSort?: string;
    data: T[] | undefined;
    highlightRow?: (args: any) => boolean;
}

export function TableStatic<T>(props: TableProps<T>) {
    const { t } = useTranslation('components', { keyPrefix: 'tables.common' });
    const [columnVisibility, setColumnVisibility] = React.useState(props.visibleColumns ?? {});
    const [globalFilter, setGlobalFilter] = React.useState('');
    const [selectedColFilters, setSelectedColFilters] = React.useState<Record<string, string[]>>({});
    const [sorting, setSorting] = React.useState<SortingState>(
        props.defaultSort ? [{ id: props.defaultSort, desc: false }] : []
    );
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
    const defaultData = React.useMemo(() => [], []);
    React.useEffect(() => {
        setColumnVisibility(props.visibleColumns ?? {});
    }, [props.visibleColumns, setColumnVisibility]);
    const table = useReactTable({
        columns: props.columns,
        data: props.data ?? defaultData,
        getCoreRowModel: getCoreRowModel(),
        debugTable: import.meta.env.NODE_ENV === 'development',
        debugHeaders: import.meta.env.NODE_ENV === 'development',
        debugColumns: import.meta.env.NODE_ENV === 'development',
        state: {
            globalFilter,
            columnFilters,
            sorting,
            columnVisibility
        },
        getFacetedUniqueValues: getFacetedUniqueValues(),
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        onColumnVisibilityChange: setColumnVisibility,
        onSortingChange: setSorting,
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        enableColumnFilters: !!props.enableColumnFilters,
        globalFilterFn: fuzzyFilter,
        filterFns: {
            fuzzy: fuzzyFilter,
            multi: multiSelectFilter
        },
        defaultColumn: {
            minSize: 0,
            size: 0
        }
    });
    const state = table.getState();
    const activeFilters = React.useMemo(() => {
        const count = globalFilter?.length ? 1 : 0;
        const colFilterCount = state.columnFilters.length ?? 0;

        return count + colFilterCount;
    }, [state]);

    React.useEffect(() => {
        const formattedColFilters = (state.columnFilters ?? []).reduce((acc, curr) => {
            return {
                ...acc,
                [curr.id]: curr.value
            };
        }, {});

        setSelectedColFilters(formattedColFilters);
    }, [activeFilters]);

    return (
        <TableTemplate<T>
            {...{ table, globalFilter, setGlobalFilter, activeFilters }}
            loading={false}
            rightControl={props.rightControl}
            noMargin={props.noMargin}
            searchable={props.searchable}
            highlightRow={props.highlightRow}
            noHeader={props.noHeader}
            enableColumnFilters={props.enableColumnFilters}
            placeholder={props.placeholder}
            // searchDisabled={!props.data?.length}
            showPagination={false}
        />
    );
}
